import { FormGroup, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { isAfter } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { ROWS_PER_PAGE } from "../App";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import AssignationTransferDialog from "../components/AssignationTransferDialog";
import AssignationPauseDialog from "../components/AssignationPauseDialog";
import { DateFieldComponent } from "../components/DatePicker";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { SelectComponent } from "../components/SelectComponent";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import { AutoCompleteComponent } from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import useAssignationList from "../hooks/useAssignationList";
import useWindowSize from "../hooks/useWindowSize";
import Assignation from "../models/Assignation";
import { Routes } from "../types/Routes";
import { AssignationStatusFilters } from "../utils/enums/filters/AssignationStatusFilters";
import OpsDateUtils from "../utils/OpsDateUtils";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import RoleUtils from "../utils/RoleUtils";
import { BooleanSegmented } from "../components/SegmentedControl";
import useTableHeight from "../hooks/useTableHeight";
import useUpdateUrl from "../hooks/useUpdateUrl";
import AssignationEditionFields from "../components/EditionDialogs/Fields/AssignationEditionFields";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { EditDialog } from "../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";

import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { useToolbarActions } from "../components/Main/ToolbarActions";
import { useStyles as formStyles } from "../components/Main/formStyles";
import { Box } from "@mui/material";
import PositionDetailsInfoButton from "../components/PositionDetailsInfoButton";
import { filtersBarStyle, Token } from "../theme";

export default function AssignationList() {
  const { toolbarActionsSlot, addButtonInToolbar } = useToolbarActions();
  const { t } = useTranslation();
  const {
    projectsNames,
    positionNames,
    userNames,
    canEdit,
    forceUpdateOn,
    onDataFetch,
    selectedAssignation,
    transferAssignation,
    assigned,
    handleChangeAssignedProjectsByPosittion,
    handleOnDeleteAssignation,
    handleOnCreate,
    handleOnUpdate,
    setSelectedAssignation,
    setTransferAssignation,
    statusFilter,
    setStatusFilter,
    isLoading,
    handleTransferAssignation,
    loadTransferBuilder,
    handleTransferProjectChange,
    transferPositionNames,
    transferProject,
    managerProjects,
    disableSaveButton,
    handlePositionSelection,
    setAssignationEndDate,
    setAssignationStartDate,
    setDisableSaveButton,
    selectedProject,
    setSelectedProject,
    setStoredProject,
    selectedPositionDetails,
    resetSelectedPosition,
  } = useAssignationList();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const { updateUrl } = useUpdateUrl(Routes.ASSIGNATIONS, searchParams);
  const [selectedEditElement, setSelectedEditElement] = useState<Assignation | undefined>(undefined);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [pauseAssignation, setPauseAssignation] = useState<Assignation | undefined>(undefined);

  const classes = useStyles();
  const customStyles = formStyles();

  const renderProjectNameAutoComplete = (
    <SimpleAutoComplete
      label="Project Name"
      items={projectsNames}
      value={selectedProject ? selectedProject : EMPTY_AUTOCOMPLETE}
      onChange={(newValue) => {
        setSelectedProject(newValue);
        searchParams.set("project", newValue.externalId);
        setStoredProject(newValue);
        updateUrl();
      }}
    />
  );

  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {renderProjectNameAutoComplete}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {renderProjectNameAutoComplete}
        {renderDialogFilters}
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      <SelectComponent
        label={"Status"}
        value={statusFilter}
        setValue={(value) => {
          setStatusFilter(value);
          searchParams.set("status", value);
          updateUrl();
        }}
        options={Object.values(AssignationStatusFilters)}
        size="small"
      />
      <FormGroup>
        <BooleanSegmented
          checked={assigned}
          onToggle={handleChangeAssignedProjectsByPosittion}
          offLabel="All"
          onLabel="Mine"
          label="Assigned"
          ariaLabel="Assigned to me"
        />
      </FormGroup>
    </>
  );

  function getAdditionalActions(instance: Assignation): ActionsMenuEntryIcons[] {
    if (!canEdit)
      return [
        {
          title: t("N/A"),
          action: () => {},
        },
      ];
    const options = [
      {
        title: t("Delete"),
        action: () => setSelectedAssignation(instance),
        icon: <DeleteOutlineIcon />,
      },
      {
        title: t("Transfer to another position"),
        action: () => setTransferAssignation(instance),
        icon: <MoveUpIcon />,
      },
      {
        title: t("Pause/Rotation"),
        action: () => setPauseAssignation(instance),
        icon: <PauseCircleOutlineIcon />,
      },
      {
        title: t("Edit"),
        action: () => {
          setSelectedEditElement(instance);
          setIsEditing(true);
        },
        icon: <EditIcon />,
      },
    ];

    return options;
  }

  function handleOnCancel(): void {
    setIsEditing(false);
  }

  return (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={
          <AppToolbar title={t("Assignation List")} rightContent={toolbarActionsSlot}>
            {renderFilters()}
          </AppToolbar>
        }
        bottomSection={
          <Box sx={{ borderRadius: 8 }}>
            <SpinnerWrapper isLoading={isLoading}>
              <TableBasedCrud<Assignation>
                headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
                buttonStyles={{
                  marginRight: 20,
                  color: "white",
                  backgroundColor: Token.actionPrimary,
                  marginBottom: 10,
                }}
                cancelButtonStyles={{
                  color: Token.textSecondary,
                  border: `1px solid ${Token.borderDefault}`,
                  marginBottom: 10,
                }}
                dialogStyles={customStyles.root}
                buttonContainer={customButtonsContainer}
                gridButtonContainer={gridButtonContainer}
                addButtonBuilder={addButtonInToolbar(customButtonBuilder)}
                maxHeight={calculateMaxHeight(canEdit)}
                stickyHeader
                stickyPagination
                elementName={t("Assignation")}
                setIsEditModeActive={setEditMode}
                rowsPerPage={ROWS_PER_PAGE}
                denseTable
                enablePagination
                stylesForHeader={{
                  fontSize: "1.1em",
                }}
                disableAutoFetch
                forceUpdateOn={forceUpdateOn}
                onDataFetch={onDataFetch}
                onCreate={canEdit ? handleOnCreate : undefined}
                onUpdate={handleOnUpdate}
                validateBeforeClosing={true}
                disableSaveButton={disableSaveButton}
                onCancelDialog={() => {
                  setDisableSaveButton(false);
                  resetSelectedPosition();
                }}
                renderDialogContent={(register, errors, control) => (
                  <>
                    {!editMode && (
                      <>
                        <AutoCompleteComponent
                          control={control}
                          register={register}
                          errors={errors}
                          propertyName={"userExternalId"}
                          label="User Name"
                          required={true}
                          options={userNames}
                        />
                        <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
                          <Box sx={{ flex: 1 }}>
                            <AutoCompleteComponent
                              control={control}
                              register={register}
                              errors={errors}
                              error={positionNames.length === 0 || undefined}
                              readOnly={positionNames.length === 0}
                              propertyName="position.externalId"
                              label={positionNames.length === 0 ? "No positions available" : "Position Name"}
                              required={true}
                              options={positionNames}
                              onChange={(data) => handlePositionSelection(data)}
                            />
                          </Box>
                          <PositionDetailsInfoButton position={selectedPositionDetails} />
                        </Box>
                      </>
                    )}
                    <AssignationEditionFields
                      control={control}
                      register={register}
                      errors={errors}
                      onStartDateChange={(date) => {
                        setAssignationStartDate(date?.toString() || "");
                      }}
                      onEndDateChange={(date) => {
                        setAssignationEndDate(date?.toString());
                      }}
                    />
                  </>
                )}
                resetValue={{ userExternalId: "", positionExternalId: "", endDate: null }}
                tableColumns={[
                  {
                    header: t("User Name"),
                    content: (instance) => (
                      <>
                        <Link to={`${Routes.PROFILE}/${instance.userExternalId}`}>{instance.userFullName}</Link>
                      </>
                    ),
                  },
                  {
                    header: t("Position"),
                    content: (instance) => (
                      <>
                        <Link to={`${Routes.POSITIONS}?project=${selectedProject?.externalId}`}>{instance.position.internalRoleName}</Link>
                      </>
                    ),
                  },
                  {
                    header: t("Internal hours"),
                    content: (instance) => <>{instance.position.internalHours}</>,
                  },
                  {
                    header: t("External hours"),
                    content: (instance) => <>{instance.position.externalHours}</>,
                  },
                  {
                    header: t("Start Date"),
                    content: (instance) => <>{OpsDateUtils.formatUTCAsDDMMMYYY(instance.shortStartDate)}</>,
                  },
                  {
                    header: t("End Date"),
                    content: (instance) => <>{instance.shortEndDate ? OpsDateUtils.formatUTCAsDDMMMYYY(instance.shortEndDate) : ""}</>,
                  },
                  {
                    header: t("Actions"),
                    content: (instance) => {
                      const actions = getAdditionalActions(instance);
                      return (
                        <>
                          {actions.map((action) => {
                            return <ActionButton onClick={action.action} tooltip={action.title} icon={action.icon} />;
                          })}
                        </>
                      );
                    },
                  },
                ]}
                stylesForRowIfCondition={(row) => {
                  if (row.shortEndDate && isAfter(new Date(), new Date(row.shortEndDate))) {
                    return { backgroundColor: Token.borderDefault };
                  }
                }}
                hideActions={true} // Hide default Actions column
                buildAdditionalActions={(instance) => {
                  return [
                    {
                      title: t("Delete"),
                      action: () => setSelectedAssignation(instance),
                    },
                    {
                      title: t("Transfer to another position"),
                      action: () => setTransferAssignation(instance),
                    },
                    {
                      title: t("Pause/Rotation"),
                      action: () => setPauseAssignation(instance),
                    },
                  ];
                }}
              />
            </SpinnerWrapper>
          </Box>
        }
      />

      {/* Dialog to delete assignations */}
      <AppDialog
        open={!!selectedAssignation}
        onClose={() => setSelectedAssignation(undefined)}
        title={"Delete"}
        content={
          <Typography>
            {`The next assignation is going to be eliminated: ${selectedAssignation?.position.externalRoleName} - ${selectedAssignation?.userFullName}.
            Do you want to confirm this action?`}
          </Typography>
        }
        buttons={[
          {
            label: "Confirm delete",
            onClick: () => {
              handleOnDeleteAssignation();
            },
          },
        ]}
      />

      {/* Dialog to manage filters  */}
      <FilterDialog title={"Assignations"} setIsOpen={setIsOpen} isOpen={isOpen}>
        {renderDialogFilters}
      </FilterDialog>

      {/* Dialog to manage assignation transfers */}
      <AssignationTransferDialog
        onClose={() => {
          setTransferAssignation(undefined);
        }}
        onSave={(formData) => {
          handleTransferAssignation(formData);
        }}
        isLoading={false}
        positionNames={transferPositionNames}
        projectsNames={RoleUtils.isAdmin() ? projectsNames : managerProjects}
        open={transferAssignation !== undefined}
        assignation={transferAssignation}
        currentProject={transferProject}
        handleChangeProject={(project: AutoCompleteUIDTO) => handleTransferProjectChange(project)}
        loadTransferBuilder={loadTransferBuilder}
      />

      <AssignationPauseDialog
        open={pauseAssignation !== undefined}
        assignation={pauseAssignation}
        onClose={() => setPauseAssignation(undefined)}
      />

      {/* Copy of the Dialog to Edit -- To use with Edit Action Icon */}
      <EditDialog<Assignation>
        headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
        buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
        cancelButtonStyles={{
          color: Token.textSecondary,
          border: `1px solid ${Token.borderDefault}`,
          marginBottom: 10,
        }}
        dialogStyles={customStyles.root}
        elementName={"Assignation"}
        dialogPosition={DialogPosition.Center}
        currentInstance={selectedEditElement}
        newItemDialogOpen={isEditing}
        onCreate={canEdit ? handleOnCreate : undefined}
        onUpdate={canEdit ? handleOnUpdate : undefined}
        onCancel={handleOnCancel}
        renderDialogContent={(register, errors, control) => (
          <>
            <AssignationEditionFields
              control={control}
              register={register}
              errors={errors}
              onStartDateChange={(date) => {
                date && setAssignationStartDate(date.toString());
              }}
              onEndDateChange={(date) => {
                date && setAssignationEndDate(date.toString());
              }}
            />
          </>
        )}
        setNewInstanceDialogOpen={() => {}}
        setLastUpdateTimestamp={() => {}}
      />
    </>
  );
}

const useStyles = makeStyles(
  () => ({
    desktopFilters: {
      ...filtersBarStyle(),
      "&>div": {
        margin: "0 !important",
      },
      "&> div:first-child": {
        minWidth: "150px",
        marginLeft: "0",
      },
      "&> div:nth-child(2)": {
        minWidth: "150px",
      },
    },
    mobileFilters: {
      "&> div:first-child": {
        minWidth: "150px",
        width: "100%",
        marginLeft: "0",
      },
      padding: "5px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      rowGap: "10px",
    },
    inputs: {
      "&.MuiInputBase-root": {
        backgroundColor: "none",
      },
    },
  }),
  { index: 1 },
);
